import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../../../services/axiosInstance";
import Spinner from "react-bootstrap/Spinner";
import { AppContext } from "../../../../services/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { extractIdFromSlug } from "../../../../utils/slugify";
import { logo } from "../../../imagepath";
import usePageTracking from "../../../../hooks/usePageTracking";
import { setVisitedPageLog } from "../../../../services/publicationanduserpagelogService";

// PDF.js imports
import { GlobalWorkerOptions } from "pdfjs-dist/build/pdf";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js Worker
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Helper to normalize broken Turkish characters/diacritics in PDF text extraction
const cleanTurkishPDFText = (str) => {
  if (!str) return str;
  let normalized = str;

  // Standard Unicode normalization (NFC) merges typical combining characters
  normalized = normalized.normalize("NFC");

  // Custom regex to handle reversed/spaced diacritics common in PDF generation
  // Cedilla (ş, Ş, ç, Ç)
  normalized = normalized.replace(/\s*[\u0327\u00B8¸]\s*([sS])/g, (m, char) => char === 'S' ? 'Ş' : 'ş');
  normalized = normalized.replace(/([sS])\s*[\u0327\u00B8¸]/g, (m, char) => char === 'S' ? 'Ş' : 'ş');
  normalized = normalized.replace(/\s*[\u0327\u00B8¸]\s*([cC])/g, (m, char) => char === 'C' ? 'Ç' : 'ç');
  normalized = normalized.replace(/([cC])\s*[\u0327\u00B8¸]/g, (m, char) => char === 'C' ? 'Ç' : 'ç');

  // Breve (ğ, Ğ)
  normalized = normalized.replace(/\s*[\u0306\u02D8˘]\s*([gG])/g, (m, char) => char === 'G' ? 'Ğ' : 'ğ');
  normalized = normalized.replace(/([gG])\s*[\u0306\u02D8˘]/g, (m, char) => char === 'G' ? 'Ğ' : 'ğ');

  // Umlaut (ö, Ö, ü, Ü)
  normalized = normalized.replace(/\s*[\u0308]\s*([oO])/g, (m, char) => char === 'O' ? 'Ö' : 'ö');
  normalized = normalized.replace(/([oO])\s*[\u0308]/g, (m, char) => char === 'O' ? 'Ö' : 'ö');
  normalized = normalized.replace(/\s*[\u0308]\s*([uU])/g, (m, char) => char === 'U' ? 'Ü' : 'ü');
  normalized = normalized.replace(/([uU])\s*[\u0308]/g, (m, char) => char === 'U' ? 'Ü' : 'ü');

  // Dotted I / Dotless I correction
  normalized = normalized.replace(/([iI])\s*[\u0307]/g, 'i');
  normalized = normalized.replace(/\s*[\u0307]\s*([iI])/g, 'i');

  return normalized;
};

// Check if a base letter is valid for a given diacritic accent
const isValidBaseCharForDiacritic = (char, diacritic) => {
  const cleanChar = char.toLowerCase();
  const cleanDia = diacritic.trim();
  
  const isCedilla = cleanDia === '̧' || cleanDia === '¸' || cleanDia === '\u0327' || cleanDia === '\u00B8';
  const isBreve = cleanDia === '̆' || cleanDia === '˘' || cleanDia === '\u0306' || cleanDia === '\u02D8';
  const isUmlaut = cleanDia === '̈' || cleanDia === '\u0308';
  const isDot = cleanDia === '̇' || cleanDia === '\u0307';
  
  if (isCedilla) {
    return cleanChar === 's' || cleanChar === 'c';
  }
  if (isBreve) {
    return cleanChar === 'g';
  }
  if (isUmlaut) {
    return cleanChar === 'o' || cleanChar === 'u';
  }
  if (isDot) {
    return cleanChar === 'i';
  }
  return false;
};

// Merge separate diacritic items and discard layout space items preceding them
const mergeTurkishDiacritics = (items) => {
  if (!items) return [];
  const mutableItems = items.map((item) => ({ ...item }));
  const result = [];
  
  const isDiacritic = (s) => {
    if (!s) return false;
    const clean = s.trim();
    return clean === '̧' || clean === '¸' || clean === '̆' || clean === '˘' || clean === '̈' || clean === '̇' ||
           clean === '\u0327' || clean === '\u00B8' || clean === '\u0306' || clean === '\u02D8' || clean === '\u0308' || clean === '\u0307';
  };
  
  for (let i = 0; i < mutableItems.length; i++) {
    const current = mutableItems[i];
    if (!current) continue;
    
    // 1. Boundary merge: Current string ends with a diacritic, next string starts with base character
    const trailingDiacriticRegex = /(\s*)((?:[\u00B8¸\u02D8˘]|\u0327|\u0306|\u0308|\u0307))$/;
    const matchTrailing = current.str.match(trailingDiacriticRegex);
    if (matchTrailing) {
      const diacritic = matchTrailing[2];
      const next = mutableItems[i + 1];
      if (next && next.str && next.str.length > 0) {
        const firstChar = next.str[0];
        if (isValidBaseCharForDiacritic(firstChar, diacritic)) {
          const combined = cleanTurkishPDFText(diacritic + firstChar);
          if (combined.length === 1) {
            current.str = current.str.slice(0, -matchTrailing[0].length);
            next.str = combined + next.str.slice(1);
            result.push(current);
            continue;
          }
        }
      }
    }
    
    // 2. Boundary merge: Current string ends with base character, next string starts with diacritic
    const leadingDiacriticRegex = /^(\s*)((?:[\u00B8¸\u02D8˘]|\u0327|\u0306|\u0308|\u0307))/;
    const next = mutableItems[i + 1];
    if (next && next.str) {
      const matchLeading = next.str.match(leadingDiacriticRegex);
      if (matchLeading) {
        const diacritic = matchLeading[2];
        const lastChar = current.str[current.str.length - 1];
        if (lastChar && isValidBaseCharForDiacritic(lastChar, diacritic)) {
          const combined = cleanTurkishPDFText(lastChar + diacritic);
          if (combined.length === 1) {
            current.str = current.str.slice(0, -1) + combined;
            next.str = next.str.slice(matchLeading[0].length);
          }
        }
      }
    }

    // 3. Fallback: Discard standalone layout spaces immediately preceding a diacritic item
    if (current.str === " ") {
      const nextItem = mutableItems[i + 1];
      if (nextItem && isDiacritic(nextItem.str)) {
        continue;
      }
    }
    
    // 4. Fallback: Standalone diacritic item merging
    const str = current.str;
    if (isDiacritic(str)) {
      // previous item
      const prev = result[result.length - 1];
      if (prev && prev.str && prev.str.length > 0) {
        const lastChar = prev.str[prev.str.length - 1];
        if (isValidBaseCharForDiacritic(lastChar, str)) {
          const combined = cleanTurkishPDFText(lastChar + str);
          if (combined.length === 1) {
            prev.str = prev.str.slice(0, -1) + combined;
            continue;
          }
        }
      }
      
      // next item
      const nextItem = mutableItems[i + 1];
      if (nextItem && nextItem.str && nextItem.str.length > 0) {
        const firstChar = nextItem.str[0];
        if (isValidBaseCharForDiacritic(firstChar, str)) {
          const combined = cleanTurkishPDFText(str + firstChar);
          if (combined.length === 1) {
            nextItem.str = combined + nextItem.str.slice(1);
            continue;
          }
        }
      }
    }
    
    result.push(current);
  }
  
  return result.filter(item => item.str !== "");
};

// --- PDFPage Component (Lazy Rendered) ---
const PDFPage = ({ pageNumber, pdfDoc, zoomLevel, rotation, onVisible, isDarkMode }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const textLayerRef = useRef(null);
  const [page, setPage] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [renderStatus, setRenderStatus] = useState("idle"); // "idle", "rendering", "rendered", "error"
  const renderTaskRef = useRef(null);

  // Intersection Observer to detect visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            onVisible(pageNumber);
          } else {
            setIsVisible(false);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: "400px 0px", // Pre-load pages before scrolling to them
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [pageNumber, onVisible]);

  // Fetch page details and standard viewport
  useEffect(() => {
    let active = true;
    const loadPage = async () => {
      try {
        const pageObj = await pdfDoc.getPage(pageNumber);
        if (!active) return;
        setPage(pageObj);
        const vp = pageObj.getViewport({ scale: 1 });
        setViewport(vp);
      } catch (err) {
        console.error(`Error loading page ${pageNumber}:`, err);
      }
    };
    loadPage();
    return () => {
      active = false;
    };
  }, [pdfDoc, pageNumber]);

  // Render to canvas
  useEffect(() => {
    if (!isVisible || !page || !viewport || !canvasRef.current) {
      // Clear canvas when out of view to save browser memory
      if (!isVisible && renderStatus === "rendered" && canvasRef.current) {
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (textLayerRef.current) {
          textLayerRef.current.innerHTML = "";
        }
        setRenderStatus("idle");
      }
      return;
    }

    const render = async () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      setRenderStatus("rendering");
      try {
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // Set higher quality rendering based on device pixel ratio
        const outputScale = window.devicePixelRatio || 1;
        const scale = zoomLevel * outputScale * 1.5;
        const vp = page.getViewport({ scale, rotation });

        canvas.width = vp.width;
        canvas.height = vp.height;

        const renderContext = {
          canvasContext: context,
          viewport: vp,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        // Render Text Layer for Text Selection
        const textLayerDiv = textLayerRef.current;
        if (textLayerDiv) {
          textLayerDiv.innerHTML = "";
          const textViewport = page.getViewport({ scale: zoomLevel, rotation });
          const textContent = await page.getTextContent();
          
          // Merge separate diacritic items and clean Turkish characters
          textContent.items = mergeTurkishDiacritics(textContent.items);

          await pdfjsLib.renderTextLayer({
            textContent,
            container: textLayerDiv,
            viewport: textViewport,
            textDivs: [],
          }).promise;
        }

        setRenderStatus("rendered");
      } catch (err) {
        if (err.name !== "RenderingCancelledException") {
          console.error(`Error rendering page ${pageNumber}:`, err);
          setRenderStatus("error");
        }
      }
    };

    render();

    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [isVisible, page, viewport, zoomLevel, rotation]);

  const ratio = viewport ? viewport.height / viewport.width : 1.414;
  const containerStyle = {
    position: "relative",
    width: "100%",
    maxWidth: viewport ? `${viewport.width * zoomLevel}px` : "800px",
    margin: "18px auto",
    backgroundColor: isDarkMode ? "#1e1e24" : "#fff",
    boxShadow: isDarkMode ? "0 4px 16px rgba(0,0,0,0.6)" : "0 4px 12px rgba(0,0,0,0.18)",
    borderRadius: "4px",
    aspectRatio: `1 / ${ratio}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "max-width 0.2s ease-out, background-color 0.3s ease",
  };

  return (
    <div ref={containerRef} style={containerStyle} id={`pdf-page-${pageNumber}`}>
      {renderStatus !== "rendered" && (
        <div className="position-absolute d-flex flex-column align-items-center justify-content-center">
          <Spinner animation="border" variant="success" size="sm" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: renderStatus === "rendered" ? "block" : "none",
          borderRadius: "4px",
          filter: isDarkMode ? "invert(0.92) hue-rotate(180deg)" : "none",
          transition: "filter 0.3s ease",
        }}
      />
      {viewport && (
        <div
          ref={textLayerRef}
          className="textLayer"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden",
            pointerEvents: "auto",
            display: renderStatus === "rendered" ? "block" : "none",
          }}
        />
      )}
    </div>
  );
};

// --- PDFThumbnail Component (Lazy Rendered) ---
const PDFThumbnail = ({ pageNumber, pdfDoc, isActive, onClick, isDarkMode }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.01,
        rootMargin: "150px 0px",
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!isVisible || isRendered) return;

    const renderThumb = async () => {
      try {
        const pageObj = await pdfDoc.getPage(pageNumber);
        if (!active) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext("2d");
        const unscaledViewport = pageObj.getViewport({ scale: 1 });
        const scale = 110 / unscaledViewport.width; // Fixed width of 110px
        const viewport = pageObj.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = pageObj.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        setIsRendered(true);
      } catch (err) {
        console.error(`Error rendering thumbnail ${pageNumber}:`, err);
      }
    };

    renderThumb();

    return () => {
      active = false;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [isVisible, pdfDoc, pageNumber, isRendered]);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "6px",
        cursor: "pointer",
        borderRadius: "4px",
        margin: "8px auto",
        width: "128px",
        transition: "background 0.2s",
        backgroundColor: isActive ? "rgba(20, 160, 119, 0.15)" : "transparent",
      }}
    >
      <div
        style={{
          border: isActive ? "3px solid #14A077" : "1px solid #555",
          boxShadow: isActive ? "0 0 10px rgba(20, 160, 119, 0.4)" : "0 2px 4px rgba(0,0,0,0.3)",
          backgroundColor: isDarkMode ? "#1e1e24" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "114px",
          height: "160px",
          overflow: "hidden",
          borderRadius: "2px",
          transition: "border 0.15s ease, background-color 0.3s ease",
        }}
      >
        {!isRendered && (
          <Spinner animation="border" variant="secondary" size="sm" />
        )}
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: isRendered ? "block" : "none",
            filter: isDarkMode ? "invert(0.92) hue-rotate(180deg)" : "none",
            transition: "filter 0.3s ease",
          }}
        />
      </div>
      <span
        style={{
          color: isActive ? "#14A077" : "#ccc",
          fontSize: "11px",
          marginTop: "6px",
          fontWeight: isActive ? "700" : "400",
          fontFamily: "sans-serif",
        }}
      >
        {pageNumber}
      </span>
    </div>
  );
};

// --- PDFViewerOpenAccess Main Component ---
const PDFViewerOpenAccess = () => {
  usePageTracking("view/pdf");
  const { slug } = useParams();
  const id = extractIdFromSlug(slug) || slug;

  const { auth } = useContext(AppContext);
  const headers = auth?.token ? { Authorization: `Bearer ${auth.token}` } : undefined;

  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [title, setTitle] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState(null);
  const [isProtected, setIsProtected] = useState(false);

  // New Custom PDF Viewer States
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pageInput, setPageInput] = useState("1");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("pdf_viewer_theme") === "dark";
  });

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("pdf_viewer_theme", next ? "dark" : "light");
      return next;
    });
  };

  const prevBlobUrlRef = useRef(null);
  const mainScrollRef = useRef(null);
  const isScrollingRef = useRef(false);
  const visitedPagesRef = useRef(new Set());

  // Detect and handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Lock body/html scroll
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Blob revoke cleanup
  useEffect(() => {
    return () => {
      if (prevBlobUrlRef.current) {
        URL.revokeObjectURL(prevBlobUrlRef.current);
      }
    };
  }, []);

  // PDF Loading and basic details fetching
  useEffect(() => {
    let alive = true;

    const loadWithToken = async () => {
      try {
        setLoading(true);

        const detailRes = await axiosInstance.get(
          `publications/search/publicationdetail/${id}/-1`,
          { headers }
        );

        if (detailRes?.data?.status !== "OK") {
          throw new Error("Yayın bilgisi getirilemedi.");
        }

        const publication = detailRes.data.data.elasticjson;
        const pdfFile = publication?.pdffile;
        const mysqlId = publication?.mysqlId;

        // Extract title
        const pubTitle =
          publication?.title ||
          publication?.name ||
          publication?.publicationTitle ||
          `Yayın #${id}`;

        if (!pdfFile || !mysqlId) {
          throw new Error("PDF dosyası bulunamadı.");
        }

        if (alive) {
          let cleanPubTitle = typeof pubTitle === 'string' ? pubTitle.replace(/\s*\/$/, '').trim() : pubTitle;
          setTitle(cleanPubTitle);
          setFileName(`${(cleanPubTitle || `yayin-${id}`)}.pdf`.replace(/[\\/:*?"<>|]+/g, "_"));
        }

        // Fetch protected/open PDF file
        const pdfUrlProtected = `publications/openaccess/pdf/displayFile/${mysqlId}`;
        const pdfRes = await axiosInstance.get(pdfUrlProtected, {
          headers,
          responseType: "arraybuffer",
        });

        // Load into PDF.js
        const loadingTask = pdfjsLib.getDocument({ data: pdfRes.data });
        const loadedPdf = await loadingTask.promise;

        if (alive) {
          setPdfDoc(loadedPdf);
          setTotalPages(loadedPdf.numPages);

          // Save blob URL for downloading & printing
          const blob = new Blob([pdfRes.data], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);

          if (prevBlobUrlRef.current) URL.revokeObjectURL(prevBlobUrlRef.current);
          prevBlobUrlRef.current = url;
          setBlobUrl(url);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) setError("Bu yayını görüntülemek için yetkiniz bulunmuyor.");
        else if (status === 404) setError("PDF bulunamadı.");
        else if (status === 500) setError("Üzgünüz, bir hata oluştu.");
        else setError(err?.message || "Beklenmeyen bir hata oluştu.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadWithToken();
    return () => {
      alive = false;
    };
  }, [id, auth?.token]);

  // Load publication title
  const loadTitle = async () => {
    try {
      const response = await axiosInstance.get(
        `publications/search/publicationdetail/${id}/-1`,
        { headers }
      );
      const attributes = response.data?.data?.elasticjson?.publicationtypeInfo || [];
      const titleAttribute = attributes.find((attr) => attr.attributename === "Başlık");

      if (titleAttribute && titleAttribute.value && titleAttribute.value.length > 0) {
        let rawTitle = titleAttribute.value[0].value[0];
        setDocumentTitle(typeof rawTitle === 'string' ? rawTitle.replace(/\s*\/$/, '').trim() : rawTitle);
      } else {
        setDocumentTitle("IDEAL Online - PDF Görüntüleyici");
      }
    } catch (err) {
      console.error("Başlık yüklenirken hata oluştu:", err);
      setDocumentTitle("IDEAL Online - PDF Görüntüleyici");
    }
  };

  useEffect(() => {
    loadTitle();
  }, [id]);

  useEffect(() => {
    if (documentTitle) {
      document.title = documentTitle;
    }
  }, [documentTitle]);

  // --- LOGGING ---
  useEffect(() => {
    let cancelled = false;

    const detectIsProtected = (pub) => {
      const flagBoolean =
        typeof pub?.protected === "boolean"
          ? pub.protected
          : typeof pub?.isProtected === "boolean"
            ? pub.isProtected
            : false;

      const flagString =
        (pub?.access || pub?.pdfaccess || pub?.pdfAccess || "").toString().toLowerCase();

      return flagBoolean || flagString === "protected" || flagString === "korumalı";
    };

    const postVisitedLog = async () => {
      try {
        if (!headers) return;

        const publicationResponse = await axiosInstance.get(
          `publications/search/publicationdetailDTO/${id}/-1`,
          { headers }
        );

        const publication = publicationResponse.data?.data?.elasticjson;

        const prot = detectIsProtected(publication);
        if (!cancelled) setIsProtected(prot);

        await createPDFLog("publication_view", prot);
      } catch (err) {
        console.error("Ziyaret/PDF log kaydı sırasında hata:", err);
      }
    };

    postVisitedLog();

    return () => {
      cancelled = true;
    };
  }, [id, auth?.token]);

  const createPDFLog = async (logtype, protFlag) => {
    try {
      if (!headers) return;
      await axiosInstance.get(
        `publicationanduserpdflog/create/${id}/${logtype}`,
        { headers }
      );
    } catch (err) {
      console.error("PDF log oluşturulurken hata oluştu:", err);
    }
  };

  // Clear visited pages log when publication ID changes
  useEffect(() => {
    visitedPagesRef.current.clear();
  }, [id]);

  // Log unique page visits in this session (0.75s read delay)
  useEffect(() => {
    if (!id || !pdfDoc) return;
    const pageNum = Number(currentPage);

    if (!visitedPagesRef.current.has(pageNum)) {
      const timer = setTimeout(() => {
        if (!visitedPagesRef.current.has(pageNum)) {
          visitedPagesRef.current.add(pageNum);
          setVisitedPageLog(id, pageNum, auth?.token).catch((err) => {
            console.error("Error logging unique page visit:", pageNum, err);
          });
        }
      }, 600); // 0.60 seconds read delay

      return () => clearTimeout(timer);
    }
  }, [currentPage, id, pdfDoc, auth?.token]);

  // --- ACTIONS ---
  const jumpToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
    setPageInput(String(pageNum));

    isScrollingRef.current = true;
    const element = document.getElementById(`pdf-page-${pageNum}`);
    if (element && mainScrollRef.current) {
      const topOffset = element.offsetTop;
      mainScrollRef.current.scrollTo({
        top: topOffset - 75,
        behavior: "auto",
      });

      // Clear scrolling lock after jump
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    }
  };

  const handlePageInputSubmit = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      const parsed = parseInt(pageInput, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
        jumpToPage(parsed);
      } else {
        setPageInput(String(currentPage));
      }
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.15, 3.0));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.15, 0.45));
  };

  const handleZoomFitWidth = async () => {
    if (!pdfDoc || !mainScrollRef.current) return;
    try {
      const page = await pdfDoc.getPage(1);
      const unscaledViewport = page.getViewport({ scale: 1, rotation });
      const containerWidth = mainScrollRef.current.clientWidth - 40;
      const targetZoom = containerWidth / unscaledViewport.width;
      setZoomLevel(Math.min(Math.max(targetZoom, 0.45), 3.0));
    } catch (e) {
      console.error(e);
    }
  };

  const handleZoomFitHeight = async () => {
    if (!pdfDoc || !mainScrollRef.current) return;
    try {
      const page = await pdfDoc.getPage(1);
      const unscaledViewport = page.getViewport({ scale: 1, rotation });
      const containerHeight = mainScrollRef.current.clientHeight - 40;
      const targetZoom = containerHeight / unscaledViewport.height;
      setZoomLevel(Math.min(Math.max(targetZoom, 0.45), 3.0));
    } catch (e) {
      console.error(e);
    }
  };

  const handleRotateClockwise = () => {
    setRotation((prev) => (prev + 90) % 360);
  };



  const toggleFullscreen = () => {
    const viewerElement = document.getElementById("pdf-viewer-workspace");
    if (!viewerElement) return;

    if (!document.fullscreenElement) {
      viewerElement.requestFullscreen().catch((err) => {
        console.error("Fullscreen error:", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const logActivity = async (type) => {
    if (!id) return;
    try {
      await axiosInstance.post(
        "publicationlogs/setVisitedLog",
        {
          publicationid: Number(id),
          visitedtype: type,
        },
        { headers }
      );
    } catch (err) {
      console.error(`Error logging ${type}:`, err);
    }
  };

  const handlePrint = () => {
    if (!blobUrl || isPrinting) return;
    setIsPrinting(true);
    logActivity("print");
    const printFrame = document.createElement("iframe");
    printFrame.style.display = "none";
    printFrame.src = blobUrl;
    document.body.appendChild(printFrame);
    printFrame.onload = () => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(printFrame);
        setIsPrinting(false);
      }, 2000);
    };
    printFrame.onerror = () => {
      document.body.removeChild(printFrame);
      setIsPrinting(false);
    };
  };

  const handleDownload = () => {
    if (!blobUrl || isDownloading) return;
    setIsDownloading(true);
    logActivity("download");
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || `yayin-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      setIsDownloading(false);
    }, 2000);
  };

  const handlePageVisible = useCallback((pageNum) => {
    if (isScrollingRef.current) return;
    setCurrentPage(pageNum);
    setPageInput(String(pageNum));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const targetTag = (e.target?.tagName || "").toLowerCase();
      if (targetTag === "input" || targetTag === "textarea") return;

      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        jumpToPage(Math.min(currentPage + 1, totalPages));
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        jumpToPage(Math.max(currentPage - 1, 1));
      } else if (e.key === "+" || (e.ctrlKey && e.key === "=")) {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === "-" || (e.ctrlKey && e.key === "-")) {
        e.preventDefault();
        handleZoomOut();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, totalPages]);

  // Loading Screen
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-light" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="success" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </div>
    );
  }

  // Error Screen
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-light" style={{ height: "100vh", padding: 20 }}>
        <div className="card shadow-lg border-danger text-center" style={{ maxWidth: 520, width: "100%" }}>
          <div className="card-header bg-danger text-white fw-bold fs-5">Bilgilendirme</div>
          <div className="card-body">
            <p className="card-text fs-5 text-danger fw-bold">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const displayTitle = (documentTitle && documentTitle !== "IDEAL Online - PDF Görüntüleyici")
    ? documentTitle
    : (title && !title.includes("Yayın #") ? title : title);

  return (
    <div
      id="pdf-viewer-container"
      className="d-flex flex-column m-0 p-0"
      style={{ height: "100vh", overflow: "hidden", backgroundColor: "#f4f6f9" }}
    >
      {/* 1. Global Page Header */}
      <div
        className="viewer-header d-flex align-items-center justify-content-center shadow-sm position-relative animate__animated animate__fadeInDown"
        style={{
          zIndex: 110,
          height: "60px",
          borderBottom: "3px solid #14A077",
          flexShrink: 0,
          backgroundColor: isDarkMode ? "#18191c" : "#ffffff",
          color: isDarkMode ? "#f1f5f9" : "#1a202c",
          transition: "background-color 0.3s ease, color 0.3s ease"
        }}
      >
        <div className="viewer-logo-link position-absolute d-flex align-items-center" style={{ left: "20px", zIndex: 11 }}>
          <Link to="/home" className="d-flex align-items-center text-decoration-none">
            <img src={logo} alt="IDEAL Online" style={{ height: "24px", objectFit: "contain" }} />
          </Link>
        </div>

        <div
          className="viewer-title text-truncate text-center px-2"
          style={{
            maxWidth: "45%",
            fontSize: "16px",
            fontWeight: "600",
            color: isDarkMode ? "#f1f5f9" : "#1a202c",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            letterSpacing: "-0.01em"
          }}
          title={displayTitle}
        >
          {displayTitle}
        </div>

        <div className="viewer-badge-container position-absolute d-flex align-items-center" style={{ right: "20px", zIndex: 11 }}>
          <span
            className="viewer-badge badge rounded-pill fw-semibold d-flex align-items-center shadow-sm"
            style={{
              backgroundColor: isDarkMode ? "#064e3b" : "#f0fff4",
              color: isDarkMode ? "#34d399" : "#14A077",
              border: isDarkMode ? "1px solid #059669" : "1px solid #14A077",
              fontSize: "11px",
              padding: "6px 14px",
              letterSpacing: "0.5px",
              textTransform: "uppercase"
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-unlock-fill me-2" viewBox="0 0 16 16">
              <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2z" />
            </svg>
            AÇIK ERİŞİM
          </span>
        </div>
      </div>

      {/* 2. Custom Classic PDF Viewer Container */}
      <div
        id="pdf-viewer-workspace"
        className="d-flex flex-column w-100"
        style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        {/* Custom Classic Toolbar */}
        <div
          className="d-flex align-items-center justify-content-between text-white"
          style={{
            height: "48px",
            backgroundColor: isDarkMode ? "#121214" : "#323639",
            borderBottom: isDarkMode ? "1px solid #27272a" : "1px solid #1c1e20",
            padding: "0 16px",
            fontSize: "14px",
            userSelect: "none",
            flexShrink: 0,
            transition: "background-color 0.3s ease"
          }}
        >
          {/* Toolbar Left: Toggle sidebar & file name */}
          <div className="d-flex align-items-center gap-3" style={{ minWidth: "20%" }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              style={{ fontSize: "20px", opacity: 0.85, transition: "opacity 0.2s" }}
              title="Kenar Çubuğu"
            >
              <i className={sidebarOpen ? "bx bx-sidebar text-success" : "bx bx-sidebar"}></i>
            </button>
            <div className="vr bg-secondary" style={{ height: "20px" }}></div>
            <span
              className="text-truncate"
              style={{
                fontSize: "13px",
                fontWeight: "500",
                color: isDarkMode ? "#cbd5e1" : "#e0e0e0",
                maxWidth: "220px",
                opacity: 0.9,
                fontFamily: "monospace"
              }}
              title={fileName}
            >
              {fileName || "document.pdf"}
            </span>
          </div>

          {/* Toolbar Center: Page Navigation & Zoom controls */}
          <div className="d-flex align-items-center gap-2">
            {/* Page Box */}
            <div className="d-flex align-items-center gap-1">
              <input
                type="text"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={handlePageInputSubmit}
                onBlur={handlePageInputSubmit}
                className="text-center text-white border-0 rounded"
                style={{
                  width: "42px",
                  height: "26px",
                  fontSize: "13px",
                  outline: "none",
                  backgroundColor: isDarkMode ? "#1e1e24" : "#212529",
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.5)"
                }}
              />
              <span style={{ color: "#aaa", fontSize: "13px" }}>/ {totalPages}</span>
            </div>

            <div className="vr bg-secondary mx-2" style={{ height: "20px" }}></div>

            {/* Zoom Controls */}
            <button
              onClick={handleZoomOut}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Uzaklaştır"
              style={{ fontSize: "18px" }}
            >
              <i className="bx bx-minus"></i>
            </button>

            <span
              className="text-center"
              style={{ width: "48px", fontSize: "13px", fontWeight: "600", color: "#ddd" }}
            >
              {Math.round(zoomLevel * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Yakınlaştır"
              style={{ fontSize: "18px" }}
            >
              <i className="bx bx-plus"></i>
            </button>

            <div className="vr bg-secondary mx-2" style={{ height: "20px" }}></div>

            {/* Fit View Options */}
            <button
              onClick={handleZoomFitWidth}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Genişliğe Sığdır"
              style={{ fontSize: "18px" }}
            >
              <i className="bx bx-expand-horizontal"></i>
            </button>

            <button
              onClick={handleZoomFitHeight}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Ekrana Sığdır"
              style={{ fontSize: "18px" }}
            >
              <i className="bx bx-expand"></i>
            </button>

            <div className="vr bg-secondary mx-2" style={{ height: "20px" }}></div>

            {/* Rotate control */}
            <button
              onClick={handleRotateClockwise}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Döndür"
              style={{ fontSize: "18px" }}
            >
              <i className="bx bx-rotate-right"></i>
            </button>
          </div>

          {/* Toolbar Right: Print, Download, Fullscreen */}
          <div className="d-flex align-items-center gap-2" style={{ minWidth: "20%", justifyContent: "flex-end" }}>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="Yazdır"
              style={{ fontSize: "18px", opacity: isPrinting ? 0.5 : 1 }}
            >
              {isPrinting ? (
                <Spinner animation="border" size="sm" variant="light" style={{ width: "16px", height: "16px" }} />
              ) : (
                <i className="bx bx-printer"></i>
              )}
            </button>

            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title="İndir"
              style={{ fontSize: "18px", opacity: isDownloading ? 0.5 : 1 }}
            >
              {isDownloading ? (
                <Spinner animation="border" size="sm" variant="light" style={{ width: "16px", height: "16px" }} />
              ) : (
                <i className="bx bx-download"></i>
              )}
            </button>

            <button
              onClick={toggleDarkMode}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title={isDarkMode ? "Aydınlık Mod" : "Karanlık Mod"}
              style={{ fontSize: "18px" }}
            >
              <i className={isDarkMode ? "bx bx-sun text-warning" : "bx bx-moon"}></i>
            </button>

            <button
              onClick={toggleFullscreen}
              className="btn btn-link text-white p-1 d-flex align-items-center justify-content-center"
              title={isFullscreen ? "Tam Ekrandan Çık" : "Tam Ekran"}
              style={{ fontSize: "18px" }}
            >
              <i className={isFullscreen ? "bx bx-exit-fullscreen text-success" : "bx bx-fullscreen"}></i>
            </button>
          </div>
        </div>

        {/* 3. Main Workspace Area (Sidebar + Document view) */}
        <div className="d-flex w-100 flex-row" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* Left Thumbnail Sidebar */}
          {sidebarOpen && (
            <div
              className="d-flex flex-column animate__animated animate__slideInLeft"
              style={{
                width: "160px",
                backgroundColor: isDarkMode ? "#18191a" : "#222425",
                borderRight: "1px solid #1c1e20",
                overflowY: "auto",
                height: "100%",
                padding: "10px 0",
                scrollbarWidth: "thin",
                scrollbarColor: isDarkMode ? "#444 #18191a" : "#555 #222425",
                transition: "background-color 0.3s ease"
              }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <PDFThumbnail
                  key={pageNum}
                  pageNumber={pageNum}
                  pdfDoc={pdfDoc}
                  isActive={currentPage === pageNum}
                  onClick={() => jumpToPage(pageNum)}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          )}

          {/* Right/Center Main Scroll Viewer */}
          <div
            ref={mainScrollRef}
            className="flex-grow-1 p-3"
            style={{
              overflow: "auto",
              height: "100%",
              backgroundColor: isDarkMode ? "#121214" : "#525659",
              scrollBehavior: "smooth",
              scrollbarWidth: "thin",
              scrollbarColor: isDarkMode ? "#555 #121214" : "#888 #525659",
              transition: "background-color 0.3s ease"
            }}
          >
            <div
              className="d-flex flex-column align-items-center mx-auto"
              style={{ width: "fit-content", minWidth: "100%" }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <PDFPage
                  key={pageNum}
                  pageNumber={pageNum}
                  pdfDoc={pdfDoc}
                  zoomLevel={zoomLevel}
                  rotation={rotation}
                  onVisible={handlePageVisible}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </div>
  );
};

export default PDFViewerOpenAccess;
